import { Fragment, useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { cn } from '../../../lib/utils';
import {
  cardClass,
  primaryText,
  secondaryText,
  semiBoldText,
  smallTextSize,
} from '../../../styling/stylingHelper';
import { AdminLayout } from './AdminLayout';
import { StatTile } from './AdminStatTile';
import {
  getSetupScore,
  setRestaurantRelevance,
  type SetupScoreResponse,
  type RestaurantSetupScore,
} from '../../../services/admin/setupScoreService';

// A flattened restaurant row carrying its client for filtering/grouping.
interface Row extends RestaurantSetupScore {
  clientId: string;
  clientName: string | null;
  clientEmail: string;
}

// A client group: its restaurants (worst-first) + the group's average (relevant only).
interface Group {
  clientId: string;
  label: string;
  restaurants: Row[];
  avgScore: number | null;
  relevantCount: number;
}

const pct = (num: number, den: number): number | null =>
  den > 0 ? Math.round((num / den) * 100) : null;

// Green ≥ 80, amber ≥ 50, red below — at-a-glance colour coding for setup completeness.
const pctTextClass = (value: number | null): string => {
  if (value === null) return 'text-gray-400 dark:text-gray-500';
  if (value >= 80) return 'text-green-600 dark:text-green-400';
  if (value >= 50) return 'text-amber-600 dark:text-amber-400';
  return 'text-red-600 dark:text-red-400';
};

// Ring tone mirrors pctTextClass bands (emerald/amber/rose track + ring + text).
const ringTone = (value: number | null) => {
  if (value === null || value < 50) return { ring: 'text-rose-500', text: 'text-rose-600 dark:text-rose-400', track: 'text-rose-100 dark:text-rose-900/30' };
  if (value < 80) return { ring: 'text-amber-500', text: 'text-amber-600 dark:text-amber-400', track: 'text-amber-100 dark:text-amber-900/30' };
  return { ring: 'text-emerald-500', text: 'text-emerald-600 dark:text-emerald-400', track: 'text-emerald-100 dark:text-emerald-900/30' };
};

/** Big donut showing the global average setup score — the live-tracker hero. */
const ScoreRing = ({ value }: { value: number | null }) => {
  const tone = ringTone(value);
  const r = 52;
  const circ = 2 * Math.PI * r;
  const v = value ?? 0;
  const dash = (Math.max(0, Math.min(100, v)) / 100) * circ;
  return (
    <div className="relative w-32 h-32 shrink-0">
      <svg viewBox="0 0 120 120" className="w-32 h-32 -rotate-90">
        <circle cx="60" cy="60" r={r} fill="none" strokeWidth="10" className={tone.track} stroke="currentColor" />
        <circle
          cx="60" cy="60" r={r} fill="none" strokeWidth="10" strokeLinecap="round"
          className={cn(tone.ring, 'transition-all duration-700 ease-out')}
          stroke="currentColor"
          strokeDasharray={`${dash} ${circ}`}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={cn('text-2xl font-bold tabular-nums', tone.text)}>
          {value === null ? '—' : `${value}%`}
        </span>
        <span className={cn(secondaryText, 'text-[10px] uppercase tracking-wide')}>set up</span>
      </div>
    </div>
  );
};

/** A completeness cell: "n/total" with its % below, coloured by completeness. */
const PctCell = ({ num, den, title }: { num: number; den: number; title: string }) => {
  const value = pct(num, den);
  return (
    <div title={title} className="leading-tight">
      <span className={cn(semiBoldText, pctTextClass(value))}>{value === null ? '—' : `${value}%`}</span>
      <span className={cn('block text-xs', secondaryText)}>{num}/{den}</span>
    </div>
  );
};

/** Yes/no flag cell (recipes / inventory) — informational, not part of the score. */
const FlagCell = ({ on, title }: { on: boolean; title: string }) =>
  on ? (
    <span title={title} className="text-green-600 dark:text-green-400 font-semibold">✓</span>
  ) : (
    <span title={title} className="text-gray-300 dark:text-gray-600">—</span>
  );

const AdminSetupScorePage = () => {
  const [data, setData] = useState<SetupScoreResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [clientFilter, setClientFilter] = useState<string>('all');
  const [showAllColumns, setShowAllColumns] = useState(false);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [savingRelevance, setSavingRelevance] = useState<Set<string>>(new Set());

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getSetupScore();
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load setup scores');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Toggle a restaurant's relevance — optimistic local update so the live tracker reacts instantly.
  const toggleRelevance = async (restaurantId: string, relevant: boolean) => {
    const apply = (value: boolean) =>
      setData((prev) =>
        prev
          ? {
              ...prev,
              clients: prev.clients.map((c) => ({
                ...c,
                restaurants: c.restaurants.map((r) =>
                  r.restaurantId === restaurantId ? { ...r, relevant: value } : r
                ),
              })),
            }
          : prev
      );
    apply(relevant);
    setSavingRelevance((prev) => new Set(prev).add(restaurantId));
    try {
      await setRestaurantRelevance(restaurantId, relevant);
    } catch {
      apply(!relevant); // revert on failure
    } finally {
      setSavingRelevance((prev) => {
        const next = new Set(prev);
        next.delete(restaurantId);
        return next;
      });
    }
  };

  // All rows flattened (respecting the client filter) — basis for the live aggregate.
  const filteredRows = useMemo<Row[]>(() => {
    if (!data) return [];
    return data.clients
      .filter((c) => clientFilter === 'all' || c.clientId === clientFilter)
      .flatMap((c) =>
        c.restaurants.map((r) => ({ ...r, clientId: c.clientId, clientName: c.clientName, clientEmail: c.clientEmail }))
      );
  }, [data, clientFilter]);

  // Live aggregate over the RELEVANT rows in view (recomputed on every relevance toggle).
  const agg = useMemo(() => {
    const relevant = filteredRows.filter((r) => r.relevant);
    const avgOf = (pick: (r: Row) => number | null) => {
      const xs = relevant.map(pick).filter((s): s is number => s !== null);
      return xs.length ? Math.round(xs.reduce((a, b) => a + b, 0) / xs.length) : null;
    };
    const scored = relevant.filter((r) => r.overall.score !== null);
    const categoryAssigned = relevant.reduce((s, r) => s + r.overall.category.assignedItems, 0);
    const categoryTotal = relevant.reduce((s, r) => s + r.overall.category.totalItems, 0);
    const costLinked = relevant.reduce((s, r) => s + r.overall.cost.linkedItems, 0);
    const costTotal = relevant.reduce((s, r) => s + r.overall.cost.totalItems, 0);
    return {
      relevantCount: relevant.length,
      totalCount: filteredRows.length,
      scoredCount: scored.length,
      avgScore: avgOf((r) => r.overall.score),
      foodAvg: avgOf((r) => r.food.score),
      drinksAvg: avgOf((r) => r.drinks.score),
      categoryPct: pct(categoryAssigned, categoryTotal),
      costPct: pct(costLinked, costTotal),
      fullyComplete: scored.filter((r) => r.overall.score === 100).length,
    };
  }, [filteredRows]);

  // Group by client; within a group sort restaurants worst-first, groups worst-average-first.
  const groups = useMemo<Group[]>(() => {
    if (!data) return [];
    const byClient = new Map<string, Row[]>();
    for (const row of filteredRows) {
      const arr = byClient.get(row.clientId) ?? [];
      arr.push(row);
      byClient.set(row.clientId, arr);
    }
    const result: Group[] = Array.from(byClient.entries()).map(([clientId, rows]) => {
      const restaurants = [...rows].sort((a, b) => {
        if (a.overall.score === null) return 1;
        if (b.overall.score === null) return -1;
        return a.overall.score - b.overall.score;
      });
      const relevantScores = restaurants
        .filter((r) => r.relevant && r.overall.score !== null)
        .map((r) => r.overall.score as number);
      const avgScore = relevantScores.length
        ? Math.round(relevantScores.reduce((s, n) => s + n, 0) / relevantScores.length)
        : null;
      const label = rows[0].clientName || rows[0].clientEmail;
      return { clientId, label, restaurants, avgScore, relevantCount: restaurants.filter((r) => r.relevant).length };
    });
    return result.sort((a, b) => {
      if (a.avgScore === null) return 1;
      if (b.avgScore === null) return -1;
      return a.avgScore - b.avgScore;
    });
  }, [data, filteredRows]);

  const toggleGroup = (clientId: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(clientId)) next.delete(clientId);
      else next.add(clientId);
      return next;
    });
  };
  const collapseAll = () => setCollapsed(new Set(groups.map((g) => g.clientId)));
  const expandAll = () => setCollapsed(new Set());

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className={cn(primaryText, 'text-2xl', semiBoldText)}>Setup Score</h2>
            <p className={cn(secondaryText, 'mt-1', smallTextSize)}>
              Live completeness tracker over dishes sold in the last {data?.windows.salesDays ?? 7} days.
              Score = 50% <span className={semiBoldText}>categories set up</span> (dishes with a real
              cost-price category, not Unassigned) + 50% <span className={semiBoldText}>cost-price set up</span>{' '}
              (the dish-count-weighted average of the food &amp; drinks cost scores — non-food/drinks
              items don't need cost setup). Suppliers, recipes &amp; inventory are informational only.
              Untick <span className={semiBoldText}>Counts</span> to drop a demo/churned restaurant out
              of the aggregate.
            </p>
          </div>

          {data && (
            <div className="flex items-center gap-2">
              <button onClick={load} className={cn('px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700', secondaryText, smallTextSize)}>
                Refresh
              </button>
              <button onClick={expandAll} className={cn('px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700', secondaryText, smallTextSize)}>
                Expand all
              </button>
              <button onClick={collapseAll} className={cn('px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700', secondaryText, smallTextSize)}>
                Collapse all
              </button>
              <button onClick={() => setShowAllColumns((v) => !v)} className={cn('px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700', secondaryText, smallTextSize)}>
                {showAllColumns ? 'Fewer columns' : 'More columns'}
              </button>
              <select
                value={clientFilter}
                onChange={(e) => setClientFilter(e.target.value)}
                className={cn('px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-800 border-none', primaryText, smallTextSize)}
              >
                <option value="all">All clients</option>
                {data.clients.map((c) => (
                  <option key={c.clientId} value={c.clientId}>
                    {c.clientName || c.clientEmail}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {loading && (
          <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 py-12 justify-center">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className={smallTextSize}>Loading setup scores…</span>
          </div>
        )}

        {error && !loading && (
          <div className={cn(cardClass, 'p-4 border-red-300 dark:border-red-700')}>
            <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
          </div>
        )}

        {!loading && !error && data && (
          <>
            {/* Live tracker hero — global average + KPI tiles */}
            <div className={cn(cardClass, 'p-5')}>
              <div className="flex items-center gap-5 flex-wrap">
                <ScoreRing value={agg.avgScore} />
                <div className="flex-1 min-w-[260px]">
                  <div className={cn(secondaryText, 'text-[10px] uppercase tracking-wide mb-2')}>
                    Overall setup completeness ({agg.relevantCount} relevant of {agg.totalCount} restaurants)
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <StatTile label="Categories set" value={agg.categoryPct === null ? '—' : `${agg.categoryPct}%`} sub="50% of the score" />
                    <StatTile label="Food cost set" value={agg.foodAvg === null ? '—' : `${agg.foodAvg}%`} sub="avg over relevant" tone="good" />
                    <StatTile label="Drinks cost set" value={agg.drinksAvg === null ? '—' : `${agg.drinksAvg}%`} sub="avg over relevant" tone="good" />
                    <StatTile label="Cost set" value={agg.costPct === null ? '—' : `${agg.costPct}%`} sub="food+drinks dishes — 50% of the score" />
                    <StatTile label="Fully complete" value={`${agg.fullyComplete}/${agg.scoredCount}`} sub="restaurants at 100%" />
                    <StatTile label="Relevant" value={String(agg.relevantCount)} sub={`of ${agg.totalCount} restaurants`} />
                  </div>
                </div>
              </div>
            </div>

            {/* Per-restaurant table */}
            <div className={cn(cardClass, 'overflow-x-auto')}>
              <table className="w-full text-left">
                <thead>
                  <tr className={cn('border-b border-gray-200 dark:border-gray-700', secondaryText)}>
                    <th className={cn('px-3 py-2 font-medium', smallTextSize)}>Restaurant</th>
                    <th className={cn('px-3 py-2 font-medium text-center', smallTextSize)}>Categories set</th>
                    <th className={cn('px-3 py-2 font-medium text-center', smallTextSize)}>Food cost set</th>
                    <th className={cn('px-3 py-2 font-medium text-center', smallTextSize)}>Drinks cost set</th>
                    {showAllColumns && (
                      <>
                        <th className={cn('px-3 py-2 font-medium text-center', smallTextSize)}>Suppliers</th>
                        <th className={cn('px-3 py-2 font-medium text-center', smallTextSize)}>Recipes</th>
                        <th className={cn('px-3 py-2 font-medium text-center', smallTextSize)}>Inventory</th>
                      </>
                    )}
                    <th className={cn('px-3 py-2 font-medium text-center', smallTextSize)}>Score</th>
                    <th className={cn('px-3 py-2 font-medium text-center', smallTextSize)}>Counts</th>
                  </tr>
                </thead>
                <tbody>
                  {groups.map((group) => {
                    const isCollapsed = collapsed.has(group.clientId);
                    return (
                      <Fragment key={group.clientId}>
                        {/* Client (admin) group header — click to toggle. */}
                        <tr
                          onClick={() => toggleGroup(group.clientId)}
                          className="cursor-pointer bg-gray-100 dark:bg-gray-800/60 border-b border-gray-200 dark:border-gray-700 hover:bg-gray-200 dark:hover:bg-gray-700/60"
                        >
                          <td colSpan={showAllColumns ? 7 : 4} className={cn('px-3 py-2', primaryText, smallTextSize, semiBoldText)}>
                            <span className="inline-flex items-center gap-1.5">
                              {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                              {group.label}
                              <span className={cn('ml-1 font-normal', secondaryText)}>
                                ({group.relevantCount}/{group.restaurants.length})
                              </span>
                            </span>
                          </td>
                          <td className="px-3 py-2 text-center">
                            <span className={cn('text-lg', semiBoldText, pctTextClass(group.avgScore))}>
                              {group.avgScore === null ? '—' : `${group.avgScore}%`}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-center" />
                        </tr>

                        {!isCollapsed &&
                          group.restaurants.map((r) => {
                            const saving = savingRelevance.has(r.restaurantId);
                            return (
                              <tr key={r.restaurantId} className={cn('border-b border-gray-100 dark:border-gray-800', !r.relevant && 'opacity-50')}>
                                <td className={cn('px-3 py-2', primaryText, smallTextSize, semiBoldText)}>{r.restaurantName}</td>
                                <td className="px-3 py-2 text-center">
                                  <PctCell num={r.overall.category.assignedItems} den={r.overall.category.totalItems} title={`${r.overall.category.assignedItems} of ${r.overall.category.totalItems} sales-active dishes have a real category`} />
                                </td>
                                <td className="px-3 py-2 text-center">
                                  <PctCell num={r.food.cost.linkedItems} den={r.food.cost.totalItems} title={`${r.food.cost.linkedItems} of ${r.food.cost.totalItems} sales-active food dishes have a cost analysis with ingredients`} />
                                </td>
                                <td className="px-3 py-2 text-center">
                                  <PctCell num={r.drinks.cost.linkedItems} den={r.drinks.cost.totalItems} title={`${r.drinks.cost.linkedItems} of ${r.drinks.cost.totalItems} sales-active drinks have a cost analysis with ingredients`} />
                                </td>
                                {showAllColumns && (
                                  <>
                                    <td className="px-3 py-2 text-center">
                                      <div title={`${r.suppliers.activeLast7Days} of ${r.suppliers.configured} suppliers delivered an order in the last 7 days — informational, not part of the score`} className="leading-tight">
                                        <span className={cn(semiBoldText, primaryText)}>
                                          {r.suppliers.activeLast7Days}/{r.suppliers.configured}
                                        </span>
                                        <span className={cn('block text-xs', secondaryText)}>active 7d</span>
                                      </div>
                                    </td>
                                    <td className="px-3 py-2 text-center">
                                      <FlagCell on={r.flags.hasRecipes} title={r.flags.hasRecipes ? 'Has recipes' : 'No recipes'} />
                                    </td>
                                    <td className="px-3 py-2 text-center">
                                      <FlagCell on={r.flags.hasInventory} title={r.flags.hasInventory ? 'Has inventory set up' : 'No inventory'} />
                                    </td>
                                  </>
                                )}
                                <td className="px-3 py-2 text-center">
                                  <span className={cn('text-lg', semiBoldText, pctTextClass(r.overall.score))}>
                                    {r.overall.score === null ? '—' : `${r.overall.score}%`}
                                  </span>
                                </td>
                                <td className="px-3 py-2 text-center">
                                  {saving ? (
                                    <Loader2 className="w-4 h-4 animate-spin text-gray-400 dark:text-textQuaternary-dark mx-auto" />
                                  ) : (
                                    <input
                                      type="checkbox"
                                      checked={r.relevant}
                                      onChange={(e) => toggleRelevance(r.restaurantId, e.target.checked)}
                                      className="cursor-pointer"
                                      title="Counts toward the aggregate setup score"
                                    />
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminSetupScorePage;
